'use client';

import { useMemo, useState, type ReactNode } from 'react';
import { useLocale } from 'next-intl';
import { useRouter } from 'next/navigation';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import SwapTextButton from '@/components/ui/SwapTextButton/SwapTextButton';
import FormConsentCheckboxes from '@/components/forms/FormConsentCheckboxes';
import LeadFormStateDistrictField, {
  LeadFormSelectSearchInput,
} from '@/components/forms/LeadFormStateDistrictField';
import { useToast } from '@/hooks/use-toast';
import { useRecaptcha } from '@/hooks/useRecaptcha';
import { usePrefillSavedLocation } from '@/hooks/useSavedLocation';
import {
  isDistrictField,
  isDynamicLeadFormComplete,
  isPincodeField,
  isStateField,
  isValidIndianMobile,
  normalizeIndianMobileInput,
  normalizeIndianPincodeInput,
  normalizePersonNameInput,
} from '@/components/forms/formValidation.utils';
import { mapEnquiryResponse } from '@/lib/enquiry-response';
import { dispatchLeadConversion } from '@/lib/analytics-events';
import { RECAPTCHA_ACTIONS } from '@/lib/recaptcha-actions';
import { stripHtml } from '@/lib/homepage-mappers';
import { cn } from '@/components/ui/utils';
import {
  isLeadFieldRequired,
  isLeadNameField,
  isLeadPhoneField,
  stripFieldLabelAsterisk,
} from '@/lib/lead-forms';
import type {
  LeadForm,
  LeadFormCheckboxField,
  LeadFormInputField,
  LeadFormSelectField,
  LeadFormTextAreaField,
  LeadFormVariant,
} from '@/types/lead-forms';

/**
 * Field styling mirrors the previous hardcoded form so the visual design
 * is preserved per the design promise. These class strings are duplicated
 * from `ContactInquiryPanel` rather than imported to keep this component
 * standalone — they should stay in lock-step on any future restyle.
 */
const inputClassName =
  'contact-enquiry-field h-11 rounded-none border-0 border-b border-black/25 bg-transparent px-0 text-sm text-black shadow-none focus-visible:border-[#ed1c22] focus-visible:ring-0';
const textareaClassName =
  'contact-enquiry-field min-h-28 resize-none rounded-none border-0 border-b border-black/25 bg-transparent px-0 py-2 text-sm text-black shadow-none focus-visible:border-[#ed1c22] focus-visible:ring-0';
const selectTriggerClassName =
  'contact-enquiry-field h-11 rounded-none border-0 border-b border-black/25 bg-transparent px-0 text-sm text-black shadow-none focus:ring-0 focus-visible:ring-0 data-[placeholder]:text-[#4c4c4c] [&>span]:text-left';
const searchableSelectContentClass =
  'max-h-72 w-[var(--radix-select-trigger-width)] rounded-md border border-black/10 bg-white text-black shadow-xl';
const CHECKBOX_CLS =
  'mt-0.5 size-[15px] rounded-[3px] border-black/25 data-[state=checked]:border-[#E31E24] data-[state=checked]:bg-[#E31E24]';
const RADIO_CLS =
  'size-[15px] border-black/25 shadow-none data-[state=checked]:border-[#E31E24] data-[state=checked]:text-[#E31E24] focus-visible:border-[#E31E24] focus-visible:ring-[#E31E24]/20';

function FieldLabel({
  htmlFor,
  required,
  children,
}: {
  htmlFor: string;
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <Label htmlFor={htmlFor} className="text-sm font-semibold text-black/80">
      {children}
      {required ? <span className="ml-0.5 text-[#E31E24]">*</span> : null}
    </Label>
  );
}

/** Maps the Strapi `inputType` enum to a native `<input type>` value. */
function nativeInputType(
  t: LeadFormInputField['inputType'],
  isPincode: boolean,
  isPhone: boolean,
): string {
  if (isPincode) return 'text';
  if (isPhone) return 'tel';
  switch (t) {
    case 'Email':
      return 'email';
    case 'Number':
      return 'number';
    case 'Phone Number':
      return 'tel';
    case 'Text':
    default:
      return 'text';
  }
}

/** Normalize raw input based on the field's semantic kind (phone/pincode/name). */
function normalizeInputValue(
  raw: string,
  { isPhone, isPincode, isName }: { isPhone: boolean; isPincode: boolean; isName: boolean },
): string {
  if (isPhone) return normalizeIndianMobileInput(raw);
  if (isPincode) return normalizeIndianPincodeInput(raw);
  if (isName) return normalizePersonNameInput(raw, 120);
  return raw;
}

function InputFieldRow({
  field,
  value,
  onChange,
}: {
  field: LeadFormInputField;
  value: string;
  onChange: (next: string) => void;
}) {
  const id = field.inputId || `field-${field.id}`;
  const isPhone = isLeadPhoneField(field);
  const isPincode = isPincodeField(field);
  const isName = isLeadNameField(field);
  const required = isLeadFieldRequired(field);
  const label = stripFieldLabelAsterisk(field.label);
  const placeholder = stripFieldLabelAsterisk(field.placeholder) || undefined;

  const resolvedPlaceholder = placeholder ?? (isPincode ? '6-digit pincode' : undefined);
  const pattern = isPhone || isPincode || isName ? undefined : (field.pattern ?? undefined);
  let maxLength: number | undefined;
  if (isPhone) {
    maxLength = 10;
  } else if (isPincode) {
    maxLength = 6;
  } else if (isName) {
    maxLength = 120;
  } else {
    maxLength = undefined;
  }
  const inputMode = isPhone || isPincode || field.inputType === 'Number' ? 'numeric' : undefined;
  let autoComplete: string | undefined;
  if (isPhone) {
    autoComplete = 'tel';
  } else if (isPincode) {
    autoComplete = 'postal-code';
  } else if (isName) {
    autoComplete = 'name';
  } else {
    autoComplete = undefined;
  }

  return (
    <div className="space-y-2">
      <FieldLabel htmlFor={id} required={required}>
        {label}
      </FieldLabel>
      <Input
        type={nativeInputType(field.inputType, isPincode, isPhone)}
        id={id}
        name={id}
        className={inputClassName}
        placeholder={resolvedPlaceholder}
        required={required}
        pattern={pattern}
        maxLength={maxLength}
        value={value}
        onChange={(e) =>
          onChange(normalizeInputValue(e.target.value, { isPhone, isPincode, isName }))
        }
        inputMode={inputMode}
        autoComplete={autoComplete}
      />
    </div>
  );
}

const asArray = <T,>(value: T[] | undefined | null): T[] => value ?? [];

/** CMS forms often include the same consent copy we render via `FormConsentCheckboxes`. */
function isStandardConsentCheckbox(label: string): boolean {
  return (
    /^\s*consent\s*$/i.test(label) ||
    /receive communications|newsletters|promotional|SMS, RCS and WhatsApp/i.test(label) ||
    /consent to Wonder Cement|Privacy Policy|privacy@wondercement/i.test(label)
  );
}

function isPrivacyConsentCheckbox(label: string): boolean {
  return label.trim().toLowerCase() === 'consent';
}

function SelectFieldRow({
  field,
  value,
  onChange,
}: {
  field: LeadFormSelectField;
  value: string;
  onChange: (next: string) => void;
}) {
  const id = field.selectId || `select-${field.id}`;
  const required = isLeadFieldRequired(field);
  const label = stripFieldLabelAsterisk(field.selectLabel);
  const placeholder = stripFieldLabelAsterisk(field.selectName) || label || 'Select an option';
  const [search, setSearch] = useState('');
  const options = asArray(field.options);
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return options;
    return options.filter((opt) =>
      (opt.optionText || opt.optionValue || '').toLowerCase().includes(term),
    );
  }, [options, search]);

  return (
    <div className="space-y-2">
      <FieldLabel htmlFor={id} required={required}>
        {label}
      </FieldLabel>
      <Select
        value={value || undefined}
        onValueChange={onChange}
        onOpenChange={(open) => {
          if (!open) setSearch('');
        }}
      >
        <SelectTrigger
          id={id}
          className={selectTriggerClassName}
          aria-required={required || undefined}
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent className={searchableSelectContentClass}>
          {options.length > 5 && (
            <LeadFormSelectSearchInput
              value={search}
              onChange={setSearch}
              placeholder={`Search ${label.toLowerCase() || 'options'}…`}
              aria-label={`Search ${label || 'options'}`}
            />
          )}
          {filtered.length > 0 ? (
            filtered.map((opt) => (
              <SelectItem key={opt.id} value={opt.optionValue ?? opt.optionText}>
                {opt.optionText}
              </SelectItem>
            ))
          ) : (
            <SelectItem value="__no-results" disabled>
              No matches found
            </SelectItem>
          )}
        </SelectContent>
      </Select>
    </div>
  );
}

function TextareaFieldRow({
  field,
  value,
  onChange,
}: {
  field: LeadFormTextAreaField;
  value: string;
  onChange: (next: string) => void;
}) {
  const id = field.textareaId || `textarea-${field.id}`;
  const required = isLeadFieldRequired(field);
  const label = stripFieldLabelAsterisk(field.label);
  const placeholder = stripFieldLabelAsterisk(field.placeholder) || undefined;
  return (
    <div className="space-y-2">
      <FieldLabel htmlFor={id} required={required}>
        {label}
      </FieldLabel>
      <Textarea
        id={id}
        name={id}
        rows={5}
        className={textareaClassName}
        placeholder={placeholder}
        required={required}
        style={field.height ? { minHeight: field.height } : undefined}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

function CheckboxRow({
  field,
  checked,
  onChange,
}: {
  field: LeadFormCheckboxField;
  checked: boolean;
  onChange: (next: boolean) => void;
}) {
  const id = field.checkboxId || `checkbox-${field.id}`;
  return (
    <div className="flex items-start gap-3">
      <Checkbox
        id={id}
        checked={checked}
        onCheckedChange={(state) => onChange(state === true)}
        className={CHECKBOX_CLS}
      />
      <Label
        htmlFor={id}
        className="text-xs leading-relaxed text-black/70 cursor-pointer font-normal"
      >
        {field.checkboxLabel}
      </Label>
    </div>
  );
}

/**
 * Merge a saved geo location into the current form values, filling only empty
 * state/district/pincode fields. Pure: returns the same object reference when
 * nothing changed (so the setState no-op short-circuits a re-render).
 */
type SavedLocation = {
  stateId?: string | null;
  districtId?: string | null;
  stateName?: string | null;
  districtName?: string | null;
  pincode?: string | null;
};

/** Fill the CRM GUID state/district dropdown keys when currently empty.
 *  Mutates `next`; returns true if anything changed. */
function mergeDropdownLocation(
  next: Record<string, string | boolean>,
  loc: SavedLocation,
  activeVariant: LeadFormVariant | undefined,
): boolean {
  const sd = asArray(activeVariant?.stateDistrictDropdown)[0];
  if (!sd) return false;

  const stateKey = sd.stateFieldId || `state-${sd.id}`;
  const districtKey = sd.districtFieldId || `district-${sd.id}`;
  const existingState = typeof next[stateKey] === 'string' ? next[stateKey] : '';
  const existingDistrict = typeof next[districtKey] === 'string' ? next[districtKey] : '';
  let changed = false;
  if (loc.stateId && !existingState) {
    next[stateKey] = loc.stateId;
    changed = true;
  }
  if (loc.districtId && !existingDistrict) {
    next[districtKey] = loc.districtId;
    changed = true;
  }
  return changed;
}

/** Fill free-text State / District / Pincode inputs when currently empty.
 *  Mutates `next`; returns true if anything changed. */
/** Resolve the fill value for a free-text location field, or null if none applies. */
function textFieldLocationValue(field: LeadFormInputField, loc: SavedLocation): string | null {
  if (isPincodeField(field) && loc.pincode) return loc.pincode;
  // Free-text state fields want the human name, not the CRM GUID.
  if (isStateField(field) && (loc.stateName || loc.stateId))
    return loc.stateName || loc.stateId || '';
  if (isDistrictField(field) && (loc.districtName || loc.districtId)) {
    return loc.districtName || loc.districtId || '';
  }
  return null;
}

function mergeTextFieldLocation(
  next: Record<string, string | boolean>,
  loc: SavedLocation,
  activeVariant: LeadFormVariant | undefined,
  leadForm: LeadForm,
): boolean {
  const textFields = [...asArray(activeVariant?.inputType), ...asArray(leadForm.nameContact)];
  let changed = false;
  for (const field of textFields) {
    const existing = typeof next[field.inputId] === 'string' ? next[field.inputId] : '';
    if (existing) continue;

    const value = textFieldLocationValue(field, loc);
    if (value !== null) {
      next[field.inputId] = value;
      changed = true;
    }
  }
  return changed;
}

function mergeSavedLocationIntoValues(
  prev: Record<string, string | boolean>,
  loc: SavedLocation,
  activeVariant: LeadFormVariant | undefined,
  leadForm: LeadForm,
): Record<string, string | boolean> {
  const next = { ...prev };
  const dropdownChanged = mergeDropdownLocation(next, loc, activeVariant);
  const textChanged = mergeTextFieldLocation(next, loc, activeVariant, leadForm);
  return dropdownChanged || textChanged ? next : prev;
}

/** Renders the `nameContact[]` block: a collapsed identity summary when
 *  name/phone were prefilled from step 1, otherwise the full input grid. */
function NameContactSection({
  leadForm,
  prefillName,
  prefillPhone,
  extraNameContactFields,
  getString,
  setValue,
}: {
  leadForm: LeadForm;
  prefillName?: string;
  prefillPhone?: string;
  extraNameContactFields: LeadFormInputField[];
  getString: (key: string) => string;
  setValue: (key: string, next: string | boolean) => void;
}) {
  const nameContact = asArray(leadForm.nameContact);
  if (nameContact.length === 0) return null;

  if (prefillName || prefillPhone) {
    return (
      <>
        <div className="flex items-center gap-3 rounded-xl border border-black/8 bg-[#fafafa] px-4 py-3">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#E31E24]/10 text-[#E31E24] text-[11px] font-black">
            {(prefillName ?? '?')[0].toUpperCase()}
          </span>
          <div className="min-w-0">
            {prefillName && (
              <p className="truncate text-[13px] font-semibold text-black/80">{prefillName}</p>
            )}
            {prefillPhone && <p className="text-[12px] text-black/40">{prefillPhone}</p>}
          </div>
          {nameContact
            .filter((f) => isLeadNameField(f) || isLeadPhoneField(f))
            .map((f) => (
              <input key={f.inputId} type="hidden" name={f.inputId} value={getString(f.inputId)} />
            ))}
        </div>
        {extraNameContactFields.length > 0 && (
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            {extraNameContactFields.map((field) => (
              <InputFieldRow
                key={field.id}
                field={field}
                value={getString(field.inputId)}
                onChange={(v) => setValue(field.inputId, v)}
              />
            ))}
          </div>
        )}
      </>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
      {nameContact.map((field) => (
        <InputFieldRow
          key={field.id}
          field={field}
          value={getString(field.inputId)}
          onChange={(v) => setValue(field.inputId, v)}
        />
      ))}
    </div>
  );
}

interface DynamicLeadFormProps {
  leadForm: LeadForm;
  prefillName?: string;
  prefillPhone?: string;
  /** Human-readable topic label sent to the CRM as `subtype`. */
  subtype?: string;
  /** Whether the phone was OTP-verified in step 1 (sent as `otpverified`). */
  phoneVerified?: boolean;
  marketingConsent?: boolean;
  onMarketingConsentChange?: (checked: boolean) => void;
  privacyConsent?: boolean;
  onPrivacyConsentChange?: (checked: boolean) => void;
  /** Fired once the enquiry is successfully submitted (lets the panel reset). */
  onSubmitted?: () => void;
}

/**
 * Renders the Step-2 form body from a Strapi `LeadForm`. Composition:
 *
 *   1. `nameContact[]` — always shown.
 *   2. If `forms.length > 1`: a radio group (labeled by
 *      `typeDropdownHeadoptional`) so the user picks a variant; the
 *      selected variant's fields render below.
 *      If `forms.length === 1`: that variant's fields render directly.
 *   3. The active variant's `inputType` / `stateDistrictDropdown` /
 *      `selectType` / `textArea` / `checkbox` (in that order).
 *   4. Submit button — wired to a stub for now (the back-end POST will be
 *      added in a follow-up; see notes in plan).
 */
export default function DynamicLeadForm({
  leadForm,
  prefillName,
  prefillPhone,
  subtype,
  phoneVerified = false,
  marketingConsent = false,
  onMarketingConsentChange,
  privacyConsent = false,
  onPrivacyConsentChange,
  onSubmitted,
}: DynamicLeadFormProps) {
  const { toast } = useToast();
  const locale = useLocale();
  const router = useRouter();
  const {
    executeRecaptcha,
    isConfigured: isRecaptchaConfigured,
    isLoaded: isRecaptchaLoaded,
  } = useRecaptcha();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState<{
    title: string;
    description: string;
  } | null>(null);

  const variants = asArray(leadForm.forms);
  const hasMultipleVariants = variants.length > 1;

  const [selectedVariantId, setSelectedVariantId] = useState<string>(
    () => variants[0]?.typeId ?? '',
  );
  const activeVariant: LeadFormVariant | undefined = useMemo(
    () => variants.find((v) => v.typeId === selectedVariantId) ?? variants[0],
    [variants, selectedVariantId],
  );

  // Single source of truth for every field value. Keyed by the field's
  // `inputId` / `selectId` / `textareaId` / `checkboxId`. Lets the renderer
  // stay stateless per row and lets us later serialize the whole form for
  // the submit endpoint.
  const [values, setValues] = useState<Record<string, string | boolean>>(() => {
    const seed: Record<string, string> = {};
    if (prefillName || prefillPhone) {
      leadForm.nameContact.forEach((f) => {
        if (prefillName && isLeadNameField(f)) seed[f.inputId] = prefillName;
        if (prefillPhone && isLeadPhoneField(f)) seed[f.inputId] = prefillPhone;
      });
    }
    return seed;
  });
  const setValue = (key: string, next: string | boolean) =>
    setValues((prev) => ({ ...prev, [key]: next }));

  /** nameContact rows not covered by step-1 name/phone prefills (email, org, location…). */
  const extraNameContactFields = useMemo(() => {
    const all = asArray(leadForm.nameContact);
    if (!prefillName && !prefillPhone) return all;
    return all.filter((f) => !(isLeadNameField(f) || isLeadPhoneField(f)));
  }, [leadForm.nameContact, prefillName, prefillPhone]);

  // Site-wide geo cache → autofill empty state/district/pincode CMS fields.
  // Handles both the CRM GUID dropdown AND free-text inputs labeled State /
  // District / Pincode. Resets when the user switches lead-form variant.
  usePrefillSavedLocation(
    (loc) => {
      setValues((prev) => mergeSavedLocationIntoValues(prev, loc, activeVariant, leadForm));
    },
    true,
    selectedVariantId,
  );

  const getString = (key: string): string => {
    const v = values[key];
    return typeof v === 'string' ? v : '';
  };
  const getBool = (key: string): boolean => {
    const v = values[key];
    return v === true;
  };

  const resolveSelectText = (field: LeadFormSelectField, value: string): string => {
    const match = asArray(field.options).find((o) => (o.optionValue ?? o.optionText) === value);
    return match?.optionText ?? value;
  };

  /**
   * Serialize the dynamic CMS fields into the fixed `CreateCaseWebsite` shape:
   * identity + subtype + consent are top-level; textareas become `remarks`;
   * a state/district pair maps to `state`/`district`; every other visible
   * field is captured losslessly in `questionsAndAnswers` (label -> answer) so
   * no CMS field is dropped regardless of how the form is configured.
   */
  function collectQuestionsAndAnswers(): {
    questionId: string;
    question: string;
    answer: string;
  }[] {
    const qa: { questionId: string; question: string; answer: string }[] = [];

    asArray(activeVariant?.inputType).forEach((f) => {
      const answer = getString(f.inputId).trim();
      if (answer)
        qa.push({
          questionId: f.inputId,
          question: stripFieldLabelAsterisk(f.label),
          answer,
        });
    });
    asArray(activeVariant?.selectType).forEach((f) => {
      const raw = getString(f.selectId).trim();
      if (raw)
        qa.push({
          questionId: f.selectId,
          question: stripFieldLabelAsterisk(f.selectLabel),
          answer: resolveSelectText(f, raw),
        });
    });

    // Also capture extra nameContact fields (org, location, email…) beyond step-1 identity.
    asArray(leadForm.nameContact).forEach((f) => {
      if (isLeadNameField(f) || isLeadPhoneField(f)) return;
      const answer = getString(f.inputId).trim();
      if (answer)
        qa.push({
          questionId: f.inputId,
          question: stripFieldLabelAsterisk(f.label),
          answer,
        });
    });

    return qa;
  }

  function buildEnquiryPayload() {
    const qa = collectQuestionsAndAnswers();

    // remarks = the free-text answer(s) only (no field label prefix).
    const remarks = asArray(activeVariant?.textArea)
      .map((f) => getString(f.textareaId).trim())
      .filter(Boolean)
      .join(' | ');

    const sd = asArray(activeVariant?.stateDistrictDropdown)[0];
    const state = sd ? getString(sd.stateFieldId || `state-${sd.id}`).trim() : '';
    const district = sd ? getString(sd.districtFieldId || `district-${sd.id}`).trim() : '';

    return {
      name: (prefillName ?? '').trim(),
      phone: normalizeIndianMobileInput(prefillPhone ?? ''),
      subtype: (subtype ?? leadForm.formName ?? 'Website Enquiry').trim(),
      state,
      district,
      remarks,
      otpVerified: phoneVerified,
      consent: Boolean(privacyConsent),
      marketingConsent: Boolean(marketingConsent),
      questionsAndAnswers: qa,
    };
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (isSubmitting) return;

    if (onPrivacyConsentChange && !privacyConsent) {
      toast({
        title: 'Consent required',
        description: 'Please accept the privacy consent to submit your enquiry.',
        variant: 'destructive',
      });
      return;
    }

    const payload = buildEnquiryPayload();
    if (!isValidIndianMobile(payload.phone)) {
      toast({
        title: 'Invalid phone number',
        description: 'Please go back and verify a valid 10-digit mobile number.',
        variant: 'destructive',
      });
      return;
    }

    setSubmissionError(null);
    setIsSubmitting(true);
    try {
      const recaptchaToken = await executeRecaptcha(RECAPTCHA_ACTIONS.dynamicLead);
      const res = await fetch('/api/wcl/contact-enquiry', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...payload,
          recaptchaToken: recaptchaToken ?? undefined,
          recaptchaAction: RECAPTCHA_ACTIONS.dynamicLead,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.error) {
        throw new Error(data.error || 'submitFailed');
      }
      const result = mapEnquiryResponse(data);
      if (result.outcome === 'duplicate') {
        const title = leadForm.thankyouDuplicateHeading?.trim() || result.title;
        const description = stripHtml(leadForm.thankyouDuplicateDescription) || result.message;
        setSubmissionError({ title, description });
        toast({ title, description, variant: 'destructive' });
        return;
      }
      if (result.outcome === 'error') throw new Error('submitFailed');

      const thankYouPath = `/${locale}/thank-you`;
      dispatchLeadConversion({
        formName: leadForm.formName?.trim() || 'contact_lead_form',
        leadSource: 'contact_page',
        leadTopic: subtype?.trim() || undefined,
        destinationPath: thankYouPath,
      });
      onSubmitted?.();
      router.push(thankYouPath);
    } catch {
      const title = leadForm.thankYouError?.trim() || 'Something went wrong';
      const description =
        stripHtml(leadForm.thankYouErrorDescription) ||
        'We couldn’t submit your enquiry. Please try again.';
      setSubmissionError({ title, description });
      toast({ title, description, variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  }

  const visibleCmsCheckboxes = useMemo(
    () =>
      asArray(activeVariant?.checkbox).filter(
        (field) => !onPrivacyConsentChange || !isStandardConsentCheckbox(field.checkboxLabel ?? ''),
      ),
    [activeVariant, onPrivacyConsentChange],
  );

  const privacyConsentHtml = useMemo(
    () =>
      asArray(activeVariant?.checkbox).find((field) =>
        isPrivacyConsentCheckbox(field.checkboxLabel ?? ''),
      )?.checkboxText,
    [activeVariant],
  );

  const canSubmit = useMemo(
    () =>
      isDynamicLeadFormComplete({
        leadFormNameContact: asArray(leadForm.nameContact),
        activeVariant,
        values,
        prefillName,
        prefillPhone,
        privacyConsent,
        marketingConsent,
        onPrivacyConsentChange,
        onMarketingConsentChange,
        visibleCmsCheckboxIds: visibleCmsCheckboxes.map((field) => field.checkboxId),
      }),
    [
      leadForm.nameContact,
      activeVariant,
      values,
      prefillName,
      prefillPhone,
      privacyConsent,
      marketingConsent,
      onPrivacyConsentChange,
      onMarketingConsentChange,
      visibleCmsCheckboxes,
    ],
  );

  return (
    <form
      data-analytics-name={leadForm.formName?.trim() || 'contact_lead_form'}
      className="space-y-4 font-figtree"
      onSubmit={handleSubmit}
    >
      {submissionError ? (
        <div
          className="rounded-xl border border-[#E31E24]/20 bg-[#E31E24]/[0.06] px-4 py-3"
          role="alert"
        >
          <p className="text-sm font-semibold text-[#B8171D]">{submissionError.title}</p>
          <p className="mt-1 text-xs leading-relaxed text-black/60">
            {submissionError.description}
          </p>
        </div>
      ) : null}
      {/* 1. nameContact[] — name/phone collapse to a summary when prefilled; other fields still render. */}
      <NameContactSection
        leadForm={leadForm}
        prefillName={prefillName}
        prefillPhone={prefillPhone}
        extraNameContactFields={extraNameContactFields}
        getString={getString}
        setValue={setValue}
      />

      {/* 2a. Variant radio group (only when there's more than one variant). */}
      {hasMultipleVariants && (
        <div className="space-y-3">
          {leadForm.typeDropdownHeadoptional && (
            <p className="text-sm font-semibold text-black/80">
              {leadForm.typeDropdownHeadoptional}
            </p>
          )}
          <RadioGroup
            value={selectedVariantId}
            onValueChange={setSelectedVariantId}
            className="flex flex-wrap gap-x-6 gap-y-2"
          >
            {variants.map((v) => (
              <div key={v.id} className="flex items-center gap-2">
                <RadioGroupItem id={`variant-${v.id}`} value={v.typeId} className={RADIO_CLS} />
                <Label
                  htmlFor={`variant-${v.id}`}
                  className={cn(
                    'cursor-pointer text-sm transition-colors',
                    selectedVariantId === v.typeId
                      ? 'font-semibold text-[#E31E24]'
                      : 'text-black/70',
                  )}
                >
                  {v.typeTitle}
                </Label>
              </div>
            ))}
          </RadioGroup>
        </div>
      )}

      {/* 2b/3. Active variant's fields. Rendered in CMS-defined order. */}
      {activeVariant && (
        <div className="space-y-6">
          {asArray(activeVariant.inputType).length > 0 && (
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              {asArray(activeVariant.inputType).map((field) => (
                <InputFieldRow
                  key={field.id}
                  field={field}
                  value={getString(field.inputId)}
                  onChange={(v) => setValue(field.inputId, v)}
                />
              ))}
            </div>
          )}

          {asArray(activeVariant.stateDistrictDropdown).map((field) => {
            const stateKey = field.stateFieldId || `state-${field.id}`;
            const districtKey = field.districtFieldId || `district-${field.id}`;
            return (
              <LeadFormStateDistrictField
                key={field.id}
                field={field}
                stateValue={getString(stateKey)}
                districtValue={getString(districtKey)}
                onStateChange={(v) => setValue(stateKey, v)}
                onDistrictChange={(v) => setValue(districtKey, v)}
              />
            );
          })}

          {asArray(activeVariant.selectType).length > 0 && (
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              {asArray(activeVariant.selectType).map((field) => (
                <SelectFieldRow
                  key={field.id}
                  field={field}
                  value={getString(field.selectId)}
                  onChange={(v) => setValue(field.selectId, v)}
                />
              ))}
            </div>
          )}

          {asArray(activeVariant.textArea).map((field) => (
            <TextareaFieldRow
              key={field.id}
              field={field}
              value={getString(field.textareaId)}
              onChange={(v) => setValue(field.textareaId, v)}
            />
          ))}

          {visibleCmsCheckboxes.map((field) => (
            <CheckboxRow
              key={field.id}
              field={field}
              checked={getBool(field.checkboxId)}
              onChange={(v) => setValue(field.checkboxId, v)}
            />
          ))}

          {onPrivacyConsentChange && (
            <FormConsentCheckboxes
              idPrefix="cp"
              showMarketing={Boolean(onMarketingConsentChange)}
              marketingConsent={marketingConsent}
              onMarketingConsentChange={onMarketingConsentChange}
              privacyConsent={privacyConsent}
              onPrivacyConsentChange={onPrivacyConsentChange}
              privacyHtml={privacyConsentHtml}
            />
          )}

          {activeVariant.submitButton && (
            <div className="pt-2">
              <SwapTextButton
                type="submit"
                text={isSubmitting ? 'Submitting' : activeVariant.submitButton.buttonText}
                disabled={
                  isSubmitting ||
                  Boolean(activeVariant.submitButton.disabled) ||
                  !canSubmit ||
                  (isRecaptchaConfigured && !isRecaptchaLoaded)
                }
                loading={isSubmitting}
                className="w-full justify-center sm:w-auto"
              />
            </div>
          )}
        </div>
      )}
    </form>
  );
}
